import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import RadioButton from '../../../Shared/RadioButton/RadioButton';
import {
  ROTH_CONVERSION_TAX_OPTIONS,
  ROTH_CONVERSION_TAX_MIN_P,
  ROTH_CONVERSION_TAX_MAX_P,
} from '../../../../settings/settings';
import {
  getRothConversionFromAsArray,
  getRothConversionTaxOption,
  getRothConversionTaxPercentage,
  getRothConversionTaxPercentageCheck,
} from '../../../../redux/modules/rothConversionTransaction/rothConversionTransaction.selectors';

import {
  STATE_TAX_DISCLOSURE_MODAL,
  STATE_WITHHOLDING_NON_MANDATORY,
} from 'src/redux/modules/modals/modals.module';

import { moduleActions } from '../../../../redux/modules';
import BaseInput from '../../../Shared/BaseInput/BaseInput';
import Button from '../../../Shared/Button/Button';
import './RothConversionTaxWithholdingForm.scss';
import {
  getMandatoryWithholdingStates,
  getNonMandatoryWithholdingStates,
  getUserStateWithholdingRules,
} from '../../../../redux/modules/stateWithholding/selectors';
import { getAutoExchangeFromSummaryFund } from 'src/redux/modules/exchangeTransaction/autoExchangeTransaction.selectors';
import If from '../../../Shared/If/If';
import {
  isCustomerWebViewEnabled,
  isSalesforceRep,
} from '../../../../redux/modules/authentication/authentication.module.selectors';
import StateIncomeTaxDisclosure from '../../../Sell/StateIncomeTaxDisclosure/StateIncomeTaxDisclosure';
import IraWithholdingWarning from '../../../Sell/IraWithholdingWarning/IraWithholdingWarning';

const RothConversionTaxWithholdingForm = () => {
  const dispatch = useDispatch();
  const { updateTaxOption, updateTaxPercentage, openModal } = moduleActions;
  const isCustomerWebView = useSelector(isCustomerWebViewEnabled);
  const fromFunds = useSelector(getRothConversionFromAsArray);
  const isSalesforceRepRoth = useSelector(isSalesforceRep);

  const selectedTaxOption = useSelector(getRothConversionTaxOption);
  const taxPercentageCheck = useSelector(getRothConversionTaxPercentageCheck);
  const taxPercentage = useSelector(getRothConversionTaxPercentage);
  const percentageError =
    !taxPercentageCheck &&
    selectedTaxOption === ROTH_CONVERSION_TAX_OPTIONS.YES_WITHHOLD;
  const mandatoryWithholdingStates = useSelector(getMandatoryWithholdingStates);
  const nonMandatoryWithholdingStates = useSelector(
    getNonMandatoryWithholdingStates
  );
  const autoExchangeFromSummaryFund = useSelector(
    getAutoExchangeFromSummaryFund
  );
  const userStateWithholdingRules = useSelector(getUserStateWithholdingRules);

  let userFundsStates = fromFunds?.map((fund) => {
    return fund?.address?.state;
  });
  userFundsStates = [...new Set(userFundsStates)];

  const mandatoryStates = userFundsStates?.filter((state) => {
    return mandatoryWithholdingStates?.includes(state);
  });
  return (
    <div className="roth-converison-tax-withholding">
      <div>
        <div className="roth-converison-tax-withholding_radio">
          <RadioButton
            category={'taxOption'}
            value={ROTH_CONVERSION_TAX_OPTIONS.YES_WITHHOLD}
            onChange={() => {
              dispatch(
                updateTaxOption(ROTH_CONVERSION_TAX_OPTIONS.YES_WITHHOLD)
              );
              if (taxPercentage === null) {
                dispatch(updateTaxPercentage(10));
              }
            }}
            className="e2e-one-to-one rothconvert-from-form-exchange-type-radio"
            checked={
              ROTH_CONVERSION_TAX_OPTIONS.YES_WITHHOLD === selectedTaxOption
            }
            labelText={<strong>Yes, withhold federal taxes</strong>}
          />
        </div>
        <div className="roth-converison-tax-withholding_desc">
          <div className="input-container">
            <BaseInput
              wrapperClassName={'roth-converison-tax-withholding_percentage'}
              placeholder={10}
              id="federal-withholding-percent-input"
              disabled={
                selectedTaxOption !== ROTH_CONVERSION_TAX_OPTIONS.YES_WITHHOLD
              }
              onInput={(e) => {
                e.target.value = e.target.value.replace(/[^0-9]*/g, '');
              }}
              value={taxPercentage}
              defaultValue={taxPercentage}
              error={
                percentageError && (
                  <p>
                    Enter value between {ROTH_CONVERSION_TAX_MIN_P}% and{' '}
                    {ROTH_CONVERSION_TAX_MAX_P}%{' '}
                  </p>
                )
              }
              displayErrorMessage={percentageError}
              displayValidationMessage={
                taxPercentageCheck || selectedTaxOption === null
              }
              validationMessage={
                <p>
                  Enter value between {ROTH_CONVERSION_TAX_MIN_P}% and{' '}
                  {ROTH_CONVERSION_TAX_MAX_P}%
                </p>
              }
              onChange={(e) => dispatch(updateTaxPercentage(e))}
            />
            <p className="roth-federal-withholding-note">
              Edit this field if you would like a rate of withholding that is
              different from the default withholding rate. See the instructions
              on page 2 of the IRS{' '}
              <a
                href="https://www.irs.gov/pub/irs-pdf/fw4r.pdf"
                rel="noreferrer"
                target="_blank"
              >
                Form W-4R{' '}
              </a>{' '}
              and the Marginal Rate Tables for additional information. Enter the
              rate as a whole number (no decimals). The following link will take
              you to the{' '}
              <a
                href={`https://www.irs.gov/pub/irs-pdf/fw4r.pdf`}
                rel="noreferrer"
                target="_blank"
              >
                {new Date().getFullYear()} Marginal Rate Tables
              </a>
              . You may use these tables to help you select the appropriate
              withholding rate for this payment or distribution. Instructions on
              how to best use them are included.
            </p>
          </div>
          <If test={!isSalesforceRepRoth}>
            <p>
              {!isCustomerWebView && isSalesforceRepRoth ? (
                <span>
                  You are choosing to withhold federal income tax and understand
                  that the rate will be done at the rate stated.
                </span>
              ) : (
                !isSalesforceRepRoth && (
                  <span>
                    I want federal income tax withheld and understand that the
                    withholding will be done at a rate of 10% unless a unless a
                    different withholding percentage is provided. If you are
                    under the age of 59 1/2, a 10% additional tax for early
                    withdrawal may apply. See IRS Publication 590-B for details
                    or consult a tax professional.
                  </span>
                )
              )}{' '}
              <If test={mandatoryStates.length === 0}>
                <Button
                  className="state-witholding-btn"
                  buttonType="secondary-slim"
                  onClick={() =>
                    dispatch(
                      openModal(STATE_WITHHOLDING_NON_MANDATORY, {
                        caller: 'tooltip',
                        state: userFundsStates[0],
                        multiState: userFundsStates.length > 1,
                        mandatoryStates: false,
                      })
                    )
                  }
                >
                  State income tax
                </Button>
              </If>
              <If
                test={mandatoryStates.length > 0 && userFundsStates.length > 1}
              >
                <Button
                  className="state-witholding-btn"
                  buttonType="secondary-slim"
                  onClick={() =>
                    dispatch(
                      openModal(STATE_WITHHOLDING_NON_MANDATORY, {
                        caller: 'tooltip',
                        state: userFundsStates,
                        multiState: userFundsStates.length > 1,
                        mandatoryStates: true,
                      })
                    )
                  }
                >
                  State income tax
                </Button>
              </If>
              <If
                test={
                  mandatoryStates.length > 0 && userFundsStates.length === 1
                }
              >
                <Button
                  className="state-witholding-btn"
                  buttonType="secondary-slim"
                  onClick={() =>
                    dispatch(
                      openModal(STATE_TAX_DISCLOSURE_MODAL, {
                        caller: 'tooltip',
                        state: mandatoryStates[0],
                      })
                    )
                  }
                >
                  State income tax
                </Button>
              </If>{' '}
              will be withheld from your distribution under the applicable state
              law if your address of record is in a state that defaults to
              withholding.
              {!isCustomerWebView && isSalesforceRepRoth && (
                <span>
                  If you are under the age of 59 1/2, a 10% additional tax for
                  early withdrawal may apply. See IRS Publication 590-B for
                  details or consult a tax professional.
                </span>
              )}
            </p>
          </If>

          <If test={!isCustomerWebView && isSalesforceRepRoth}>
            <div className={'market-volatility-note-box'}>
              <div className={'market-volatility-note'}>
                <IraWithholdingWarning
                  caller={null}
                  closeIraWithholdingWarning={null}
                  isSalesforceRepMessage={true}
                  setInputFocus={null}
                />
              </div>
            </div>
            <If test={!autoExchangeFromSummaryFund?.nonRetirementLike}>
              <div className={'market-volatility-note-box'}>
                <div className={'market-volatility-note'}>
                  <StateIncomeTaxDisclosure
                    closeStateIncomeTaxDisclosure={null}
                    isSalesforceRepMessage={true}
                    mandatoryWithholdingStates={mandatoryWithholdingStates}
                    nonMandatoryWithholdingStates={
                      nonMandatoryWithholdingStates
                    }
                    stateWithholdingRules={userStateWithholdingRules}
                    state={userFundsStates[0]}
                  />
                </div>
              </div>
            </If>
          </If>
        </div>
      </div>
      <div>
        <div className="roth-converison-tax-withholding_radio">
          <RadioButton
            category={'taxOption'}
            value={ROTH_CONVERSION_TAX_OPTIONS.NO_DONT_WITHHOLD}
            onChange={() =>
              dispatch(
                updateTaxOption(ROTH_CONVERSION_TAX_OPTIONS.NO_DONT_WITHHOLD)
              )
            }
            className="e2e-one-to-one rothconvert-from-form-exchange-type-radio"
            checked={
              ROTH_CONVERSION_TAX_OPTIONS.NO_DONT_WITHHOLD === selectedTaxOption
            }
            labelText={<strong>No, do not withhold federal taxes</strong>}
          />
        </div>
        <div className="roth-converison-tax-withholding_desc">
          <p>
            {!isCustomerWebView && isSalesforceRepRoth ? (
              <span>
                You understand that you may be responsible for payment of
                estimated tax and may incur penalties under the estimated tax
                rules if the withholding and estimated tax payments are not
                sufficient.
              </span>
            ) : (
              <span>
                I understand that I may be responsible for payment of estimated
                tax and may incur penalties under the estimated tax rules if my
                withholding and estimated tax payments are not sufficient.
              </span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default RothConversionTaxWithholdingForm;
